import {
	TypeBrandLink,
	TypeLink,
	TypeLinkFields,
	TypeNavigation,
	TypeNavigationFields,
	TypePromotion,
	TypeStoreLocatorFields
} from "../../../../types/contentful";

import { Entry } from "contentful";
import { getLinkHref, isNavigation } from "../../../helpers";
import { TypeCardPromotion } from "../../../../types/contentful/TypeCardPromotion";
import { promoClicked } from "../../useSegment";
import {
	ProductsSearch,
	SetProductSearchParams,
	StoreContent
} from "../../../../types/ProductsSearch";
import { documentToHtmlString } from "@contentful/rich-text-html-renderer";
import { Document } from "@contentful/rich-text-types";

export const getBrandLinks = (
	brandLinks: TypeBrandLink[] | TypeLink[],
	vendor?: string
) => {
	const brandLinksTransformed = brandLinks.map(
		(brandLink: TypeBrandLink | TypeLink) => {
			try {
				const item = brandLink?.fields;
				const mediaItem = item?.media?.fields;

				if ("isActiveBrand" in item) {
					return {
						imageUrl: mediaItem?.file.url,
						imageMobileUrl: item?.mediaMobile?.fields?.file.url,
						href: getLinkHref(item.url, vendor),
						altText: item.linkAltText,
						target: item.target.toLowerCase(),
						isFocused: item.isActiveBrand
					};
				}

				return {
					imageUrl: mediaItem?.file.url,
					href: getLinkHref(item.url, vendor),
					altText: item.linkAltText,
					target: item.target.toLowerCase(),
					linkBodyText: item.linkBodyText
				};
			} catch (ex) {
				// eslint-disable-next-line no-console
				console.error("unprocessable brand link", brandLink, ex);
			}
		}
	);

	return brandLinksTransformed.filter((bl) => bl);
};

export const getBrandLinksJsonLd = (brandLinks: any[]) => {
	return brandLinks.map((link) => ({
		"@context": "http://schema.org",
		"@type": "SiteNavigationElement",
		"name": link.altText,
		"url": link.href,
		"description": "Visit " + link.altText
	}));
};

export const getLinks = (
	links: TypeLink[],
	localeCase: (text?: string) => string | undefined,
	vendor?: string
) => {
	const linksTransformed = links.map((link: TypeLink) => {
		try {
			const item = link?.fields;

			const parsedLink = {
				href: getLinkHref(item.url, vendor),
				children: localeCase(item.linkBodyText),
				iconName: item.icon,
				target: item.target.toLowerCase()
			};
			// vendor links need an id so that vendors are able to change the URL in their page
			const vendorLinks = ["header-my-account", "header-cart"];
			// eslint-disable-next-line no-console
			console.log("vendor", vendor);
			if (vendor !== "ricoh" && vendorLinks.includes(item.title)) {
				return {
					...parsedLink,
					id: item.title
				};
			}
			return parsedLink;
		} catch (ex) {
			// eslint-disable-next-line no-console
			console.error("unprocessable utility bar link", link, ex);
		}
	});

	return linksTransformed.filter((to) => to);
};

export const getLinksJsonLd = (links: any[]) => {
	return links.map((link) => ({
		"@context": "http://schema.org",
		"@type": "SiteNavigationElement",
		"name": link.children,
		"url": link.href,
		"description": "Visit " + link.children
	}));
};

export const getBrandLogo = (link?: TypeLinkFields, vendor?: string) => {
	if (!link) {
		return null;
	}
	try {
		return {
			imageUrl: link.media?.fields.file.url,
			href: getLinkHref(link.url, vendor),
			altText: link.linkAltText,
			target: convertTargetForIframe(link.target),
			linkBodyText: link.linkBodyText
		};
	} catch (ex) {
		// eslint-disable-next-line no-console
		console.error("unprocessable brand logo", link, ex);
	}
};

export const getBrandLogoJsonLd = (brandLogo: any) => {
	return {
		"@context": "http://schema.org",
		"@type": "SiteNavigationElement",
		"name": brandLogo.altText,
		"url": brandLogo.href,
		"description": "Visit " + brandLogo.altText
	};
};

type HeaderNavigation = TypeLink | TypeNavigation;

const convertTargetForIframe = (target: string | undefined) => {
	if (!target) return;
	if (target === "Internal") return "_top";
	return "_blank";
};
const promoLinkClicked = (evt: any, data: any) => {
	promoClicked({
		creative: data.foregroundImage?.src,
		destination_url: evt.currentTarget.href,
		link_location: "header promo",
		name: data.title
	});
};
export const getHeaderLinks = (
	input: HeaderNavigation[],
	localeCase: (text?: string) => string | undefined,
	vendor?: string
) => {
	const transformedOutput = input.map((entry: HeaderNavigation) => {
		try {
			if (entry?.sys?.contentType?.sys?.id === "link") {
				const navigation = entry.fields as TypeLinkFields;

				return {
					title: localeCase(navigation.linkBodyText),
					id: navigation.title.split(" ").join("-"),
					url: getLinkHref(navigation.url, vendor),
					target: convertTargetForIframe(navigation.target)
				};
			} else {
				const navigation = entry.fields as TypeNavigationFields;

				if (navigation) {
					const hierarchy: any[] = [];
					const promotions: any[] = [];

					if (navigation.items) {
						navigation.items.forEach((item: Entry<TypeNavigationFields>) => {
							try {
								if (isNavigation(item)) {
									const navigationItem = item.fields;

									const subHierarchy =
										navigationItem.items &&
										navigationItem.items.map((subItem) => {
											const subItemL2 = subItem.fields as TypeLinkFields;
											return {
												title: localeCase(subItemL2.linkBodyText),
												url: getLinkHref(subItemL2.url, vendor),
												target: convertTargetForIframe(subItemL2.target)
											};
										});
									let viewAll = null;
									if (navigationItem.viewAll)
										viewAll = {
											title: localeCase(
												navigationItem.viewAll?.fields.linkBodyText
											),
											url: getLinkHref(
												navigationItem.viewAll?.fields.url,
												vendor
											),
											target: convertTargetForIframe(
												navigationItem.viewAll?.fields.target
											)
										};
									hierarchy.push({
										type: "menu",
										title: localeCase(navigationItem.titleText),
										titleText: localeCase(navigationItem.titleText),
										viewAll: viewAll,
										id: navigationItem.title.split(" ").join("-"),
										hierarchy: subHierarchy
									});
								}

								if (item.sys.contentType?.sys.id === "link") {
									const returnItem = item as TypeLink;

									hierarchy.push({
										title: localeCase(returnItem.fields.linkBodyText),
										url: getLinkHref(returnItem.fields.url, vendor),
										target: convertTargetForIframe(returnItem.fields.target)
									});
								}

								if (item.sys.contentType?.sys.id === "promotion") {
									const promotionItem = item as TypePromotion;
									const returnItem = promotionItem.fields;
									promotions.push({
										title: localeCase(returnItem.titleText),
										image: returnItem.link?.fields.media?.fields.file.url,
										description: returnItem.description,
										link: {
											title: localeCase(returnItem.link?.fields.linkBodyText),
											href: getLinkHref(returnItem.link?.fields.url, vendor),
											iconName: returnItem.link?.fields.icon,

											target: convertTargetForIframe(
												returnItem.link?.fields.target
											)
										}
									});
								}

								if (item.sys.contentType?.sys.id === "cardPromotion") {
									const promotionItem = item as TypeCardPromotion;
									const returnItem = promotionItem.fields;
									const parsedPromo = {
										title: localeCase(returnItem.title),
										bodyCopy: returnItem.bodyCopy,
										link: returnItem?.link
											? {
													text: localeCase(
														returnItem.link?.fields.linkBodyText
													),
													href: getLinkHref(
														returnItem.link?.fields?.url,
														vendor
													),
													icon: returnItem.link?.fields?.icon,
													target: returnItem.link?.fields?.target,
													altText: returnItem.link?.fields?.linkAltText,
													onClick: (evt: any) => promoLinkClicked(evt, null)
											  }
											: undefined,
										foregroundImage: {
											src: returnItem?.foregroundImage?.fields?.file?.url,
											alt:
												returnItem?.foregroundImage?.fields?.description ||
												returnItem?.foregroundImage?.fields?.title
										},
										backgroundImage: returnItem?.backgroundImage
											? {
													src: returnItem?.backgroundImage?.fields?.file?.url,
													alt:
														returnItem?.backgroundImage?.fields?.description ||
														returnItem?.backgroundImage?.fields?.title
											  }
											: undefined
									};
									if (parsedPromo.link) {
										parsedPromo.link.onClick = (evt: any) =>
											promoLinkClicked(evt, parsedPromo);
									}

									promotions.push(parsedPromo);
								}
							} catch (ex) {
								// eslint-disable-next-line no-console
								console.error("unprocessable navigation entry", item, ex);
							}
						});
					}
					let viewAll = null;
					if (navigation.viewAll)
						viewAll = {
							title: localeCase(navigation.viewAll?.fields.linkBodyText),
							url: getLinkHref(navigation.viewAll?.fields.url, vendor),
							target: convertTargetForIframe(navigation.viewAll?.fields.target)
						};
					return {
						title: localeCase(navigation.titleText),
						titleText: localeCase(navigation.titleText),
						id: navigation.title.split(" ").join("-"),
						viewAll: viewAll,
						hierarchy,
						promotions
					};
				}
			}
		} catch (ex) {
			// eslint-disable-next-line no-console
			console.error("unprocessable header link", entry, ex);
		}
	});

	return transformedOutput.filter((to) => to);
};

export const getHeaderLinksJsonLd = (headerLinks: any[]): any[] => {
	const jsonLd: any[] = [];

	headerLinks?.forEach((nav) => {
		if (nav.promotions) {
			nav.promotions.forEach((promo: any) => {
				jsonLd.push({
					"@context": "http://schema.org",
					"@type": "SiteNavigationElement",
					"name": promo.title,
					"url": promo.url || promo?.link?.href,
					"description": "Visit " + promo.title
				});
			});
		}

		if (nav.hierarchy) {
			return nav.hierarchy.forEach((linkHierarchy: any) => {
				if (linkHierarchy.hierarchy) {
					return linkHierarchy.hierarchy.forEach((linkHierarchy: any) => {
						jsonLd.push({
							"@context": "http://schema.org",
							"@type": "SiteNavigationElement",
							"name": linkHierarchy.title,
							"url": linkHierarchy.url,
							"description": "Visit " + linkHierarchy.title
						});
					});
				}

				jsonLd.push({
					"@context": "http://schema.org",
					"@type": "SiteNavigationElement",
					"name": linkHierarchy.title,
					"url": linkHierarchy.url,
					"description": "Visit " + linkHierarchy.title
				});
			});
		}

		return {
			"@context": "http://schema.org",
			"@type": "SiteNavigationElement",
			"name": nav.title,
			"url": nav.url,
			"description": "Visit " + nav.title
		};
	});

	return jsonLd;
};

export const getStoreContent = (
	content: Entry<TypeStoreLocatorFields>,
	vendor?: string
): StoreContent => {
	const parseStoreLink = (data?: TypeLinkFields) =>
		data
			? {
					...data,
					url: getLinkHref(data.url, vendor)
			  }
			: undefined;

	return {
		title: content.fields.title.split(" ").join("-"),
		directionsLink: parseStoreLink(content.fields.directionsLink?.fields),
		storeDetailsLink: parseStoreLink(content.fields.storeDetailsLink?.fields),
		storeFinderButtonLink: content.fields.storeFinderButtonLink?.fields,
		storeSearchErrorSubText: documentToHtmlString(
			content.fields.storeSearchErrorSubText as Document
		)
	};
};

export const getProductResults = (
	{
		localePart,
		handle,
		product_url,
		id,
		productIndex,
		...product
	}: SetProductSearchParams,
	vendor?: string
): ProductsSearch => {
	const productUrl = product_url
		? product_url
		: `${process.env.REACT_APP_SHOPIFY_STORE_DOMAIN}${localePart}products/${handle}`;

	return {
		...product,
		name: product.title,
		imageUrl: product.product_image,
		imageAltText: product.title,
		price: product.variants_min_price,
		productUrl: getLinkHref(productUrl, vendor) || productUrl,
		variant: product.variant_title,
		regular_price: product.compare_at_price,
		url: productUrl,
		product_id: id ? id.toString() : "",
		min_price: product.variants_min_price,
		max_price: product.variants_max_price,
		category: `${localePart}products/${handle}`,
		position: productIndex + 1
	};
};
